import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import ChartJs from 'chart.js/auto';
import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import React, { useRef } from 'react';

import styles from './Altitude.module.scss';

import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

// X축 구간 수
const TICK_COUNT = 60;

const Altitude = ({ relAltitude = 0 }) => {
  const relAltitudeRef = useRef(relAltitude);
  const chartRef = useRef();
  const chart = useRef();

  useMountEffect(() => {
    const intervalId = setInterval(drawChart, 1000);

    return () => {
      clearInterval(intervalId);
    };
  });

  useUpdateEffect(() => {
    relAltitudeRef.current = relAltitude;
  }, [relAltitude]);

  const drawChart = () => {
    // 차트 존재 시
    if (chart.current) {
      updateChart();
    }
    // 차트 미존재 시
    else {
      const initialData = Array(TICK_COUNT).fill(null);

      const context = chartRef.current.getContext('2d');
      chart.current = new ChartJs(context, {
        type: 'line',
        data: {
          labels: Array(TICK_COUNT).fill(''),
          datasets: [
            {
              label: 'Relative Altitude',
              data: initialData.slice(),
              borderColor: getCSSVariable('--tint-color'),
              borderWidth: 1,
              pointRadius: 1,
              pointBackgroundColor: getCSSVariable('--tint-color'),
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          scales: {
            xAxis: { display: false },
            yAxis: {
              grid: {
                color: getCSSVariable('--line-color'),
                drawTicks: false,
                lineWidth: 0.5,
              },
              ticks: {
                color: 'white',
                font: { size: 8 },
                stepSize: 10,
                precision: 0,
              },
            },
          },
          plugins: { legend: false },
          animation: { easing: 'linear' },
        },
      });
    }
  };

  const updateChart = () => {
    const { labels, datasets } = chart.current.data;

    // X축 구간 수 동일한 경우, 첫 번째 데이터 제거
    if (TICK_COUNT === labels.length) {
      labels.shift();
      datasets.forEach((dataset) => dataset.data.shift());
    }

    labels.push('');
    datasets.forEach((dataset) => dataset.data.push(relAltitudeRef.current));

    // 차트 반영
    chart.current.update();
  };

  return (
    <div className={cx('container')}>
      <div className={cx('chartWrapper')}>
        <div className={cx('title')}>
          Relative Altitude
          <div className={cx('value')}>
            {commaNumber(relAltitude.toFixed(2))}
            <span className={cx('unit')}>m</span>
          </div>
        </div>
        <div className={cx('chart')}>
          <canvas ref={chartRef} />
        </div>
      </div>
    </div>
  );
};

export default Altitude;
